import { StateCreator } from 'zustand';
import {
  fetchOwnedProfiles,
  fetchProfileGroups,
  fetchProfiles,
  ProfileGroupSummary,
} from '../../profiles/ProfilesApi';
import { StoredOrg } from '../../groups/GroupsApi';
import { AccessLevel } from '../../utils/constants';
import { isRequestCanceled } from '../../utils/errors';

export interface StoredProfile {
  profile_id: string;
  name: string;
  avatar: string;
  level: AccessLevel;
  is_owner?: boolean;
  role?: string;
  age?: number | null;
  language?: string;
}

type ProfileSummaryPatch = Partial<Omit<StoredProfile, 'profile_id'>>;
export type ProfileListStatus = 'idle' | 'loading' | 'ready' | 'error';

const normalizeLevel = (v: any): AccessLevel => {
  if (v === 'admin' || v === AccessLevel.ADMIN) return AccessLevel.ADMIN;
  if (v === 'full' || v === AccessLevel.FULL) return AccessLevel.FULL;
  return AccessLevel.STANDARD;
};

const mergeProfileSummary = (
  profile: StoredProfile,
  patch: ProfileSummaryPatch
): StoredProfile => ({
  ...profile,
  ...patch,
  level:
    patch.level !== undefined ? normalizeLevel(patch.level) : profile.level,
});

const getFirstOrganization = (
  groups: ProfileGroupSummary[]
): StoredOrg | null => groups.find((group) => group.org?.org_id)?.org ?? null;

const getActiveProfileGroupsState = (
  profileId: string | null,
  groups: ProfileGroupSummary[]
) => ({
  activeProfileGroups: groups,
  activeProfileGroupsLoadedProfileId: profileId,
  activeProfileGroupsStatus: 'ready' as const,
  activeProfileOrg: getFirstOrganization(groups),
});

const getResetActiveProfileGroupsState = () => ({
  activeProfileGroups: [],
  activeProfileGroupsStatus: 'idle' as const,
  activeProfileGroupsLoadedProfileId: null,
  activeProfileOrg: null,
});

export interface ProfileAccessState {
  profiles: StoredProfile[];
  ownedProfiles: StoredProfile[];
  ownedProfilesStatus: ProfileListStatus;

  activeProfileId: string | null;
  activeProfile: StoredProfile | null;
  activeProfileGroups: ProfileGroupSummary[];
  activeProfileGroupsStatus: ProfileListStatus;
  activeProfileGroupsLoadedProfileId: string | null;
  activeProfileOrg: StoredOrg | null;

  setProfiles: (profiles: StoredProfile[]) => void;
  updateProfileSummary: (profileId: string, patch: ProfileSummaryPatch) => void;
  setActiveProfileId: (id: string | null) => void;
  setOwnedProfiles: (profiles: StoredProfile[]) => void;
  removeActiveProfileGroup: (profileId: string, groupId: string) => () => void;
  resetActiveProfileGroups: () => void;

  fetchProfiles: () => Promise<void>;
  fetchOwnedProfiles: () => Promise<void>;
  fetchActiveProfileGroups: (
    profileId?: string | null,
    signal?: AbortSignal
  ) => Promise<void>;
}

export const createProfileAccessStore: StateCreator<ProfileAccessState> = (
  set,
  get
) => {
  let profilesRequestVersion = 0;
  let ownedRequestVersion = 0;
  let groupsRequestVersion = 0;

  return {
    profiles: [],
    ownedProfiles: [],
    ownedProfilesStatus: 'idle',
    activeProfileId: null,
    activeProfile: null,
    ...getResetActiveProfileGroupsState(),

    setProfiles: (profiles) => {
      profilesRequestVersion += 1;
      const { activeProfileId } = get();

      const activeProfile = activeProfileId
        ? (profiles.find((p) => p.profile_id === activeProfileId) ?? null)
        : null;

      if (activeProfileId && !activeProfile) groupsRequestVersion += 1;

      set({
        profiles,
        activeProfile,
        ...(activeProfileId && !activeProfile
          ? getResetActiveProfileGroupsState()
          : {}),
      });
    },
    updateProfileSummary: (profileId, patch) =>
      set((state) => {
        let updatedProfileFromList: StoredProfile | null = null;
        let updatedOwnedProfile: StoredProfile | null = null;
        const profiles = state.profiles.map((profile) => {
          if (profile.profile_id !== profileId) {
            return profile;
          }

          updatedProfileFromList = mergeProfileSummary(profile, patch);
          return updatedProfileFromList;
        });
        const ownedProfiles = state.ownedProfiles.map((profile) => {
          if (profile.profile_id !== profileId) {
            return profile;
          }

          updatedOwnedProfile = mergeProfileSummary(profile, patch);
          return updatedOwnedProfile;
        });
        const activeProfile =
          state.activeProfileId === profileId
            ? state.activeProfile
              ? mergeProfileSummary(state.activeProfile, patch)
              : (updatedProfileFromList ?? updatedOwnedProfile)
            : state.activeProfile;

        return { profiles, ownedProfiles, activeProfile };
      }),
    setOwnedProfiles: (ownedProfiles) => set({ ownedProfiles }),
    setActiveProfileId: (id) => {
      const { profiles, activeProfileId: previousActiveProfileId } = get();

      const activeProfile = id
        ? (profiles.find((p) => p.profile_id === id) ?? null)
        : null;

      if (previousActiveProfileId !== id) groupsRequestVersion += 1;

      set({
        activeProfileId: id,
        activeProfile,
        ...(previousActiveProfileId !== id
          ? getResetActiveProfileGroupsState()
          : {}),
      });
    },
    removeActiveProfileGroup: (profileId, groupId) => {
      const previous = get();
      if (
        previous.activeProfileId !== profileId ||
        previous.activeProfileGroupsLoadedProfileId !== profileId
      )
        return () => {};

      const activeProfileGroups = previous.activeProfileGroups.filter(
        (group) => group.group_id !== groupId
      );
      set({
        activeProfileGroups,
        activeProfileOrg: getFirstOrganization(activeProfileGroups),
      });

      // Undo only this optimistic snapshot. Keep any newer read's status and
      // request version intact so its eventual success or failure still applies.
      return () =>
        set((state) => {
          if (
            state.activeProfileId !== profileId ||
            state.activeProfileGroupsLoadedProfileId !== profileId ||
            state.activeProfileGroups !== activeProfileGroups
          )
            return state;
          return {
            activeProfileGroups: previous.activeProfileGroups,
            activeProfileOrg: previous.activeProfileOrg,
          };
        });
    },
    resetActiveProfileGroups: () => {
      groupsRequestVersion += 1;
      set(getResetActiveProfileGroupsState());
    },

    fetchProfiles: async () => {
      const version = ++profilesRequestVersion;
      try {
        const res = await fetchProfiles();
        if (version !== profilesRequestVersion) return;
        const profiles: StoredProfile[] = (res.data as any[]).map((p) => ({
          ...p,
          level: normalizeLevel(p.level),
        }));
        get().setProfiles(profiles);
      } catch (err) {
        if (version !== profilesRequestVersion) return;
        console.error('Error while fetching profiles:', err);
        get().setProfiles([]);
      }
    },

    fetchOwnedProfiles: async () => {
      const version = ++ownedRequestVersion;
      set({ ownedProfilesStatus: 'loading' });
      try {
        const res = await fetchOwnedProfiles();
        const ownedProfiles = res.data.map((p) => ({
          ...p,
          level: normalizeLevel(p.level),
        }));
        if (version === ownedRequestVersion)
          set({ ownedProfiles, ownedProfilesStatus: 'ready' });
      } catch (err) {
        if (version === ownedRequestVersion)
          set({
            ownedProfilesStatus: isRequestCanceled(err) ? 'idle' : 'error',
          });
      }
    },

    fetchActiveProfileGroups: async (profileId, signal) => {
      const targetProfileId = profileId ?? get().activeProfileId;

      if (!targetProfileId) {
        get().resetActiveProfileGroups();
        return;
      }
      if (get().activeProfileId !== targetProfileId) return;

      const version = ++groupsRequestVersion;
      const isCurrent = () =>
        version === groupsRequestVersion &&
        get().activeProfileId === targetProfileId;
      set({ activeProfileGroupsStatus: 'loading' });

      try {
        const res = await fetchProfileGroups(targetProfileId, signal);
        if (!isCurrent() || signal?.aborted) return;

        set(getActiveProfileGroupsState(targetProfileId, res.data));
      } catch (err) {
        if (!isCurrent()) return;
        set({
          activeProfileGroupsStatus: isRequestCanceled(err) ? 'idle' : 'error',
        });
      }
    },
  };
};
